import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/lib/settings";
import { mediaImage } from "@/lib/media";

export default function SEOHead({ title, description, keywords, ogImage, schema, noindex = false }) {
  const { settings } = useSettings();
  const loc = useLocation();
  const defaults = settings?.seo_defaults || {};
  const finalTitle = title || defaults.title || "Happy Retreats";
  const finalDesc = description || defaults.description || "";
  const finalKeywords = keywords || defaults.keywords || "";
  const finalOg = mediaImage(ogImage) || mediaImage(defaults.og_image) || "";

  useEffect(() => {
    document.title = finalTitle;

    // Clean canonical: origin + pathname only (no query strings, hashes or malformed suffixes)
    const cleanPath = loc.pathname.replace(/\/+$/, "") || "/";
    const canonicalUrl = `${window.location.origin}${cleanPath === "/" ? "/" : cleanPath}`;

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content || "");
    };

    setMeta('meta[name="description"]', { create: { name: "description" }, content: finalDesc });
    setMeta('meta[name="keywords"]', { create: { name: "keywords" }, content: finalKeywords });
    setMeta('meta[property="og:title"]', { create: { property: "og:title" }, content: finalTitle });
    setMeta('meta[property="og:description"]', { create: { property: "og:description" }, content: finalDesc });
    setMeta('meta[property="og:type"]', { create: { property: "og:type" }, content: "website" });
    setMeta('meta[property="og:url"]', { create: { property: "og:url" }, content: canonicalUrl });
    if (finalOg) setMeta('meta[property="og:image"]', { create: { property: "og:image" }, content: finalOg });
    setMeta('meta[name="twitter:card"]', { create: { name: "twitter:card" }, content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { create: { name: "twitter:title" }, content: finalTitle });
    setMeta('meta[name="twitter:description"]', { create: { name: "twitter:description" }, content: finalDesc });
    if (finalOg) setMeta('meta[name="twitter:image"]', { create: { name: "twitter:image" }, content: finalOg });

    // robots — noindex for 404/error pages so search engines never index them
    let robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }

    // canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (noindex) {
      if (canonical) canonical.remove();
    } else {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    }

    // schema
    document.querySelectorAll('script[data-hr-schema="1"]').forEach((s) => s.remove());
    if (schema) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.hrSchema = "1";
      s.textContent = typeof schema === "string" ? schema : JSON.stringify(schema);
      document.head.appendChild(s);
    }
  }, [finalTitle, finalDesc, finalKeywords, finalOg, schema, noindex, loc.pathname]);

  return null;
}
